import { createContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { flushSync } from 'react-dom';
import type { UserDTO, LoginRequestDTO, RegisterRequestDTO } from '@/types/auth';
import { useUIStore } from '@/store/useUIStore';
import { authService } from '@/services/auth';
import { getAuthToken } from '@/utils/auth';
import { establishClientSession, normalizeAuthRole } from '@/lib/postAuthSession';

interface AuthContextType {
  user: UserDTO | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginRequestDTO) => Promise<any>;
  logout: () => Promise<void>;
  register: (data: RegisterRequestDTO) => Promise<any>;
  /** Atualiza o usuário em memória + storage (ex.: após troca de e-mail). */
  updateSessionUser: (user: UserDTO) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

function normalizeUser(user: UserDTO): UserDTO {
  return {
    ...user,
    role: normalizeAuthRole(user.role) as UserDTO['role'],
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserDTO | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const addNotification = useUIStore((state) => state.addNotification);

  const validateSession = async (userId: string) => {
    if (!userId || userId === 'undefined') return;
    try {
      const response = await authService.getUser(userId);
      if (response) {
        const normalized = normalizeUser(response);
        setUser(normalized);
        // Mantém Context e localStorage alinhados (evita UI admin com role DOCTOR no storage)
        if (typeof window !== 'undefined') {
          const token = getAuthToken();
          if (token) {
            establishClientSession({
              accessToken: token,
              user: normalized,
              markPatientChecklistPending: false,
            });
          } else {
            localStorage.setItem('user', JSON.stringify(normalized));
            localStorage.setItem('user_role', normalized.role);
          }
        }
      }
    } catch (err) {
      console.error('[AuthContext] 🔐 Session validation failed:', err);
    }
  };

  const loadUser = useCallback(async () => {
    try {
      setIsLoading(true);
      if (typeof window !== 'undefined') {
        const savedUser = localStorage.getItem('user');
        const savedToken = getAuthToken();

        if (savedUser && savedToken) {
          try {
            const parsedUser = normalizeUser(JSON.parse(savedUser));
            setUser(parsedUser);
            setIsLoading(false);
            validateSession(parsedUser.id);
            return;
          } catch (parseError) {
            console.error('[AuthContext] ❌ Failed to parse user:', parseError);
          }
        }
      }
      setIsLoading(false);
    } catch (err) {
      console.error('[AuthContext] ❌ Error loading user:', err);
      setUser(null);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (credentials: LoginRequestDTO) => {
    try {
      setError(null);
      setIsLoading(true);
      const data = await authService.login(credentials);
      const normalizedUser = normalizeUser(data.user);
      flushSync(() => {
        setUser(normalizedUser);
        setIsLoading(false);
      });
      addNotification({ type: 'success', title: 'Login realizado', message: 'Bem-vindo à plataforma!' });
      return { ...data, user: normalizedUser };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      addNotification({ type: 'error', title: 'Erro no login', message: errorMessage });
      setIsLoading(false);
      throw err;
    }
  };

  const register = async (data: RegisterRequestDTO) => {
    try {
      setError(null);
      const responseData = await authService.register(data);
      const normalizedUser = normalizeUser(responseData.user);
      flushSync(() => {
        setUser(normalizedUser);
        setIsLoading(false);
      });
      addNotification({ type: 'success', title: 'Conta criada', message: 'Bem-vindo à plataforma!' });
      return { ...responseData, user: normalizedUser };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      addNotification({ type: 'error', title: 'Erro no registro', message: errorMessage });
      throw err;
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      authService.logout();
      setUser(null);
      setError(null);
      addNotification({ type: 'success', title: 'Desconectado', message: 'Você foi desconectado com sucesso' });
    } catch (err) {
      console.error('Failed to logout:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateSessionUser = (nextUser: UserDTO) => {
    const normalized = normalizeUser(nextUser);
    setUser(normalized);
    if (typeof window !== 'undefined') {
      const token = getAuthToken();
      if (token) {
        establishClientSession({
          accessToken: token,
          user: normalized,
          markPatientChecklistPending: false,
        });
      } else {
        localStorage.setItem('user', JSON.stringify(normalized));
        localStorage.setItem('user_role', normalized.role);
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        error,
        login,
        logout,
        register,
        updateSessionUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
